/**
 * Audio Player Bar Component
 * Fixed bottom bar with playback controls (quran.com style)
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Play,
  Pause,
  SkipBack,
  SkipForward,
  Volume2,
  VolumeX,
  X,
  Loader2,
} from 'lucide-react';

import type { Reciter } from '@/types/quran';

interface AudioPlayerBarProps {
  /** The <audio> element ref managed by ReadPage */
  audioRef: React.RefObject<HTMLAudioElement | null>;
  isPlaying: boolean;
  isAudioLoading: boolean;
  onTogglePlay: () => void;
  onStop: () => void;
  /** Optional: skip to previous/next page */
  onSkipBack?: () => void;
  onSkipForward?: () => void;
  /** Reciter selection */
  reciters?: Reciter[];
  selectedReciterId?: string;
  onReciterChange?: (reciterId: string) => void;
}

function formatTime(seconds: number): string {
  if (!isFinite(seconds) || seconds < 0) return '00:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export function AudioPlayerBar({
  audioRef,
  isPlaying,
  isAudioLoading,
  onTogglePlay,
  onStop,
  onSkipBack,
  onSkipForward,
  reciters = [],
  selectedReciterId,
  onReciterChange,
}: AudioPlayerBarProps) {
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [muted, setMuted] = useState(false);
  const rafRef = useRef<number>(0);

  // Track currentTime via requestAnimationFrame
  const tick = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      setCurrentTime(audio.currentTime);
      if (audio.duration && isFinite(audio.duration)) {
        setDuration(audio.duration);
      }
    }
    rafRef.current = requestAnimationFrame(tick);
  }, [audioRef]);

  useEffect(() => {
    if (isPlaying) {
      rafRef.current = requestAnimationFrame(tick);
    } else {
      cancelAnimationFrame(rafRef.current);
    }
    return () => cancelAnimationFrame(rafRef.current);
  }, [isPlaying, tick]);

  // Reset when audio element changes
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) {
      setCurrentTime(0);
      setDuration(0);
    }
  }, [audioRef.current]);

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current;
    if (!audio || !isFinite(audio.duration)) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    audio.currentTime = ratio * audio.duration;
  };

  const handleMuteToggle = () => {
    const audio = audioRef.current;
    if (audio) {
      audio.muted = !audio.muted;
      setMuted(!muted);
    }
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className="audio-player-bar">
      {/* Progress bar */}
      <div className="audio-progress-bar" onClick={handleProgressClick}>
        <div
          className="audio-progress-fill"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Controls row */}
      <div className="audio-controls">
        {/* Left: time */}
        <span className="audio-time">{formatTime(currentTime)}</span>

        {/* Center controls */}
        <div className="flex items-center gap-1">
          <button
            className="audio-btn"
            onClick={handleMuteToggle}
            title={muted ? 'เปิดเสียง' : 'ปิดเสียง'}
          >
            {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
          </button>

          <button
            className="audio-btn"
            onClick={onSkipBack}
            title="อายะห์ก่อนหน้า"
          >
            <SkipBack size={18} />
          </button>

          <button
            className="audio-btn audio-btn-play"
            onClick={onTogglePlay}
            disabled={isAudioLoading}
            title={isAudioLoading ? 'กำลังโหลด...' : isPlaying ? 'หยุดชั่วคราว' : 'เล่นต่อ'}
          >
            {isAudioLoading ? (
              <Loader2 size={22} className="animate-spin" />
            ) : isPlaying ? (
              <Pause size={22} />
            ) : (
              <Play size={22} />
            )}
          </button>

          <button
            className="audio-btn"
            onClick={onSkipForward}
            title="อายะห์ถัดไป"
          >
            <SkipForward size={18} />
          </button>

          <button className="audio-btn" onClick={onStop} title="ปิด">
            <X size={18} />
          </button>
        </div>

        {/* Right: reciter select */}
        {reciters.length > 0 && onReciterChange ? (
          <select
            value={selectedReciterId ?? ''}
            onChange={(e) => onReciterChange(e.target.value)}
            className="text-xs bg-transparent text-gray-400 border-none outline-none cursor-pointer max-w-[90px] truncate"
            title="เลือกผู้อ่าน"
          >
            {reciters.map((r) => (
              <option key={r.id} value={r.id} className="bg-gray-900 text-white">
                {r.name}
              </option>
            ))}
          </select>
        ) : (
          <span className="audio-time text-right">{formatTime(duration)}</span>
        )}
      </div>
    </div>
  );
}

export default AudioPlayerBar;
